import { getNumElements } from './util';

/*
 * Direct-apply sink for worker tick batches: buffer updates land in the host
 * cache + appSnapshot (updateWebGLBuffers' work) without a per-batch React
 * cycle. Every batch is APPLIED immediately (slices are never dropped -- the
 * conflation-postmortem rule); only notification coalesces: one upload/render
 * per painted frame, one summary emission per tick for React. The renderer
 * component attaches its context per props update and detaches on unmount;
 * unattached, emissions fall through to the React path.
 */
let ctx = null;
let lastNodeLen = -1;

export function attachTickSink(next) {
    //a fresh renderState starts with empty numElements: reset the length
    //gate so the first batch re-seeds it (same node count or not)
    if (!ctx || ctx.renderingScheduler.renderState !== next.renderingScheduler.renderState) {
        lastNodeLen = -1;
    }
    ctx = next;
}

export function detachTickSink() {
    ctx = null;
    lastNodeLen = -1;
}

export function applyTickBatch(emission) {
    if (!ctx ||
        !emission || !emission.nodesUpdate || !emission.nodesUpdate.nfos) {
        return false;
    }
    const { renderingScheduler, $falcor } = ctx;
    //derived from the scheduler so the renderState written here is BY
    //CONSTRUCTION the one its appSnapshot below belongs to
    const { renderer, renderState } = renderingScheduler;
    const buffers = emission.nodesUpdate.nfos;
    renderer.loadBuffers(renderState, buffers);

    //length changes are structural (first tick, filter): only then do the
    //numElements + falcor writes updateWebGLBuffers repeated per batch
    const len = emission.nodesTableLength;
    if (len !== lastNodeLen) {
        lastNodeLen = len;
        getNumElements(renderState.config, 'node', len).forEach(([name, numE]) =>
            renderer.setNumElements(renderState, name, numE));
        if ($falcor) {
            $falcor.withoutDataSource().set({ json: { points: { elements: len } } }).subscribe();
        }
    }

    const snap = renderingScheduler.appSnapshot;
    snap.vboUpdated = true;
    for (const name in buffers) {
        if (name in snap.buffers) {
            snap.buffers[name] = renderState.hostBuffersCache[name];
        }
        if (name in snap.bufferReceivedVersions) {
            snap.bufferReceivedVersions[name] = (snap.bufferReceivedVersions[name] || 0) + 1;
        }
    }
    //tag-deduped; the drain's trigger coalescing renders once per frame.
    //During simulation (the only time the worker runs) the trigger is
    //unconditionally renderSceneFast, mirroring renderer.render()'s
    //(renderFast || simulating) selection.
    renderingScheduler.renderScene('vboupdate', { trigger: 'renderSceneFast' });
    return true;
}
